import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, MapPinIcon, SparklesIcon, CheckCircle2Icon, MessageSquareIcon, PhoneIcon, ChevronLeftIcon, ChevronRightIcon, HeartIcon } from 'lucide-react';
import { ListingCardProps } from './ListingCard';
import { MerchantBadge } from './MerchantBadge';
import { merchantFor, LISTING_SELLERS } from './../data/merchants';
interface ListingDetailModalProps {
  listing: ListingCardProps;
  open: boolean;
  onClose: () => void;
}

/**
 * Cars and homes are decided on the photograph plus a handful of numbers, so
 * the gallery holds a full-height column of its own and the facts sit beside
 * it — never below the fold. The panel is ordered the way the decision is
 * made: what it is, where it is, what it costs, the four numbers that matter,
 * what Talab makes of it, then how to act.
 */
export function ListingDetailModal({
  listing,
  open,
  onClose
}: ListingDetailModalProps) {
  const [frame, setFrame] = useState(0);
  const [saved, setSaved] = useState(listing.isSaved ?? false);
  const isProperty = listing.sector === 'property';
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);
  const merchant = merchantFor(LISTING_SELLERS[listing.id] ?? listing.location, isProperty ? 'Real estate agency' : listing.sellerType === 'Individual' ? 'Private seller' : 'Car dealer');
  const step = (dir: 1 | -1) => setFrame(f => (f + dir + listing.images.length) % listing.images.length);
  const [amount, period] = listing.price.split('/');
  /** A period in the price means it is rented, which changes the whole panel. */
  const isRental = Boolean(period);
  const summary = isProperty ? ['Priced in line with recent sales in this community', 'Title deed and ownership verified', 'Service charges disclosed by the agency', 'Agency responds within a few hours'] : isRental ? ['Fair daily rate for this class of vehicle', 'Comprehensive insurance included', 'Deposit refunded on return', 'Seller response rate is high'] : ['Fair price based on current market comparison', 'GCC specifications confirmed', 'Mileage is reasonable for the model year', 'Seller response rate is high'];
  const terms = isProperty ? 'Viewings seven days a week · Ownership documents available on request · Agency fee 2%' : isRental ? 'Free cancellation up to 24 hours before pick-up · Unlimited kilometres · Comprehensive insurance included · Delivery available' : 'Inspection welcome · Finance and trade-in available · Transfer handled by the seller';
  const cta = isProperty ? 'Arrange a viewing' : isRental ? 'Book now' : 'Reserve this car';
  return createPortal(<AnimatePresence data-id="element-2616" data-mp="2sczfxjzznw93">
      {open && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.18,
      ease: 'easeOut'
    }} className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-6" data-id="element-2617" data-mp="2sdr9yqdxtlx9">
          <div className="absolute inset-0 bg-dark-navy/50 backdrop-blur-sm" onClick={onClose} data-id="element-2618" data-mp="3b8pj3ajnqyok" />

          <motion.div initial={{
        opacity: 0,
        scale: 0.97,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.97,
        y: 20
      }} transition={{
        duration: 0.24,
        ease: [0.23, 1, 0.32, 1]
      }} role="dialog" aria-modal="true" aria-label={listing.title} className="relative bg-white w-full sm:max-w-5xl rounded-t-3xl sm:rounded-3xl shadow-large overflow-hidden flex flex-col sm:flex-row max-h-[94vh] sm:h-[620px]" data-id="element-2619" data-mp="7m8o2wypldw1">
            {/* Gallery — a column of its own, not a banner */}
            <div className="relative flex-shrink-0 h-52 sm:h-auto sm:w-[56%] bg-slate-100 group" data-id="element-2620" data-mp="2l2e4ycyoq3y1">
              <img src={listing.images[frame]} alt={listing.title} className="w-full h-full object-cover" data-id="element-2621" data-mp="3hkadkfi4okm8" />

              {listing.matchScore > 0 && <span className="absolute top-4 left-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/95 backdrop-blur-sm shadow-sm" data-id="element-2622" data-mp="zbfwdeez7u0e">
                  <SparklesIcon className="w-4 h-4 text-ai-purple" data-id="element-2623" data-mp="18b2mv5663feo" />
                  <span className="text-[13px] font-bold bg-gradient-to-r from-primary-indigo to-ai-purple bg-clip-text text-transparent" data-id="element-2624" data-mp="189rjh6gmw9o3">
                    {listing.matchScore}% Match
                  </span>
                </span>}

              {listing.images.length > 1 && <>
                  <button onClick={() => step(-1)} aria-label="Previous image" className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/85 backdrop-blur-sm flex items-center justify-center text-slate-700 shadow-sm hover:bg-white transition-colors duration-150 ease-out" data-id="element-2625" data-mp="1n60ge1d1yaxc">
                    <ChevronLeftIcon className="w-5 h-5" data-id="element-2626" data-mp="25z4ebyxaz9xr" />
                  </button>
                  <button onClick={() => step(1)} aria-label="Next image" className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/85 backdrop-blur-sm flex items-center justify-center text-slate-700 shadow-sm hover:bg-white transition-colors duration-150 ease-out" data-id="element-2627" data-mp="5xd9jxf0cvyv">
                    <ChevronRightIcon className="w-5 h-5" data-id="element-2628" data-mp="ombi40b2cwd6" />
                  </button>

                  <div className="absolute bottom-4 left-4 flex items-center gap-1.5" data-id="element-2629" data-mp="km101p9zyfhy">
                    {listing.images.map((_, i) => <button key={i} onClick={() => setFrame(i)} aria-label={`Image ${i + 1}`} className={`h-1.5 rounded-full transition-all duration-200 ease-out ${i === frame ? 'w-6 bg-white' : 'w-1.5 bg-white/60'}`} data-id="element-2630" data-mp="ykqxj60qah6u" data-mpi={i} />)}
                  </div>
                  <span className="absolute bottom-3.5 right-4 px-2 py-1 rounded-md bg-dark-navy/65 backdrop-blur-sm text-[11px] font-bold text-white" data-id="element-2631" data-mp="1awjnm537xr4x">
                    {frame + 1} / {listing.images.length}
                  </span>
                </>}
            </div>

            {/* The facts, in decision order */}
            <div className="flex-1 flex flex-col min-w-0" data-id="element-2632" data-mp="162p1kv05502o">
              <button onClick={onClose} aria-label="Close" className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-white/90 backdrop-blur-sm sm:bg-transparent flex items-center justify-center text-slate-400 hover:text-dark-navy transition-colors duration-150 ease-out" data-id="element-2633" data-mp="s3z43e9esyds">
                <XIcon className="w-5 h-5" data-id="element-2634" data-mp="n80exog9bccb" />
              </button>

              <div className="flex-1 overflow-y-auto px-6 py-6 pr-12 space-y-5" data-id="element-2635" data-mp="2cm5g0rtpd45a">
                <div data-id="element-2636" data-mp="2hivw3f5ibzqp">
                  <h2 className="font-display font-bold text-[26px] leading-tight text-dark-navy" data-id="element-2637" data-mp="2hjf4a45cv9rj">
                    {listing.title}
                  </h2>
                  <p className="inline-flex items-center gap-1.5 text-[14px] text-slate-500 mt-2" data-id="element-2638" data-mp="10ayz9rrzqjd5">
                    <MapPinIcon className="w-4 h-4 text-slate-400" data-id="element-2639" data-mp="vk8ry90086ft" />
                    {listing.location}
                  </p>
                  <p className="mt-3" data-id="element-2640" data-mp="qkw9e0g61lj1">
                    <span className="font-display font-bold text-[30px] leading-none bg-gradient-to-r from-primary-indigo to-ai-purple bg-clip-text text-transparent" data-id="element-2641" data-mp="qjuu1js4acvi">
                      {amount}
                    </span>
                    {period && <span className="text-[15px] font-semibold text-slate-400" data-id="element-2642" data-mp="cmpi535q2jsc">
                        /{period}
                      </span>}
                  </p>
                </div>

                {/* Four quiet tiles — no borders fighting the rounded corners */}
                {listing.specs.length > 0 && <div className="grid grid-cols-2 gap-2.5" data-id="element-2643" data-mp="21z1b0mswpjb7">
                    {listing.specs.slice(0, 4).map(spec => <div key={spec.label} className="bg-slate-50 rounded-xl px-4 py-3 min-w-0" data-id="element-2644" data-mp="2eiyamzhthutf">
                        <span className="block text-[11px] font-bold uppercase tracking-wider text-slate-400" data-id="element-2645" data-mp="2jcj3ye8gu0ma">
                          {spec.label}
                        </span>
                        <span className="block text-[15px] font-bold text-dark-navy truncate mt-1" data-id="element-2646" data-mp="1qnc7dksknl3o">
                          {spec.value}
                        </span>
                      </div>)}
                  </div>}

                <div data-id="element-2647" data-mp="25jtf821jyhlh">
                  <h3 className="inline-flex items-center gap-2 text-[12px] font-bold uppercase tracking-wider text-ai-purple" data-id="element-2648" data-mp="25kcwrhnaj3yz">
                    <SparklesIcon className="w-4 h-4" data-id="element-2649" data-mp="3c16r6fcrz02h" />
                    Talab AI summary
                  </h3>
                  <ul className="space-y-2.5 mt-3" data-id="element-2650" data-mp="3jlphbtv9e6ol">
                    {summary.map(item => <li key={item} className="flex items-start gap-2.5 text-[14px] text-slate-700 leading-snug" data-id="element-2651" data-mp="3nmsx6gdb42pt">
                        <CheckCircle2Icon className="w-[18px] h-[18px] text-success flex-shrink-0 mt-0.5" data-id="element-2652" data-mp="3sg3wzxjpbj88" />
                        {item}
                      </li>)}
                  </ul>
                </div>

                <div className="flex items-center justify-between gap-3 pt-1" data-id="element-2653" data-mp="34neyjl4mu3sz">
                  <MerchantBadge merchant={merchant} showKind data-id="element-2654" data-mp="39njhdht0jamd" />
                  <button onClick={() => setSaved(s => !s)} aria-label={saved ? 'Saved' : 'Save'} className="w-9 h-9 rounded-full border border-slate-200 flex items-center justify-center text-slate-400 hover:text-error hover:border-error/40 transition-colors duration-150 ease-out flex-shrink-0" data-id="element-2655" data-mp="39lym1pyvjnng">
                    <HeartIcon className={`w-4 h-4 ${saved ? 'fill-error text-error' : ''}`} data-id="element-2656" data-mp="2ecv2xo1cigid" />
                  </button>
                </div>
              </div>

              <div className="px-6 pb-5 pt-4 border-t border-slate-100 flex-shrink-0" data-id="element-2657" data-mp="24ms95lmegyiz">
                <div className="flex items-center gap-2.5" data-id="element-2658" data-mp="1zoidl9wrjvwu">
                  <button className="flex-1 px-5 py-3.5 rounded-2xl bg-gradient-to-r from-primary-indigo to-ai-purple text-white text-[15px] font-bold hover:brightness-110 transition-[filter] duration-150 ease-out" data-id="element-2659" data-mp="1vnezosya09y9">
                    {cta}
                  </button>
                  <button aria-label="Message the seller" className="w-12 h-12 rounded-2xl border border-slate-200 flex items-center justify-center text-slate-500 hover:text-primary-indigo hover:border-primary-indigo/40 transition-colors duration-150 ease-out flex-shrink-0" data-id="element-2660" data-mp="1lrldehur923a">
                    <MessageSquareIcon className="w-5 h-5" data-id="element-2661" data-mp="19ere7jut7fq4" />
                  </button>
                  <button aria-label="Call the seller" className="w-12 h-12 rounded-2xl border border-slate-200 flex items-center justify-center text-slate-500 hover:text-primary-indigo hover:border-primary-indigo/40 transition-colors duration-150 ease-out flex-shrink-0" data-id="element-2662" data-mp="1e5zr4pvclj81">
                    <PhoneIcon className="w-5 h-5" data-id="element-2663" data-mp="1s6skh79wy89k" />
                  </button>
                </div>
                <p className="text-[12px] text-slate-400 leading-relaxed mt-3" data-id="element-2664" data-mp="1x3sqji6rjb0o">
                  {terms}
                </p>
              </div>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>, document.body);
}